/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file linkedinApi.js
 * @description
 * Networking layer of the mobile LinkedIn module — a REST mirror of the admin's
 * `src/networking/linkedinNetworking.js` (same method names). The backend talks to LinkedIn through a
 * hosted-auth provider (Unipile): the user connects their own LinkedIn account once, after which Happy
 * can send/receive direct messages and search people and companies on their behalf.
 *
 * Every call goes through {@link module:helpers/WorkSheetContext.apiFetch} (bearer token + API prefix
 * injected) against `/linkedin/*` as published by `linkedin.swagger.yaml`. Failures REJECT with an Error
 * carrying `status` and `code`; a user that has not connected LinkedIn yields `code ===
 * 'LINKEDIN_NOT_CONNECTED'` (HTTP 409) which {@link isNotConnectedError} recognises.
 *
 * Realtime fan-out: the `linkedin:message` socket event cannot reach a screen directly, so the
 * module-level emitter below ({@link subscribeLinkedinEvents}) lets the socket owner broadcast and any
 * screen listen without prop drilling.
 */

import {Linking, Platform} from 'react-native';
import {apiFetch} from '../helpers/WorkSheetContext';
import {getManagementScope} from '../helpers/managementApi';
import {getAccessToken} from '../helpers/storage';
import {ApiConfig} from '../config/ApiConfig';
import {buildAttachmentFormData} from './entityDocumentsApi';

/** Backend error code of an account that has not connected LinkedIn. */
export const LINKEDIN_NOT_CONNECTED = 'LINKEDIN_NOT_CONNECTED';

/** Deep link the hosted auth returns to (app scheme from app.json). */
export const LINKEDIN_RETURN_URL = 'happy-app://linkedin/connected';

const listeners = new Set();

/**
 * Subscribe to LinkedIn module events (`message`, `status`).
 * @param {(event: {type: string, payload?: object}) => void} listener
 * @returns {() => void} Unsubscribe.
 */
export const subscribeLinkedinEvents = (listener) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};

/**
 * Broadcast a LinkedIn module event to every subscriber.
 * @param {string} type Event type.
 * @param {object} [payload] Event payload.
 * @returns {void}
 */
export const emitLinkedinEvent = (type, payload) => {
    listeners.forEach((listener) => {
        try {
            listener({type, payload});
        } catch (_e) {
            // a faulty subscriber must never break the others
        }
    });
};

/**
 * @param {*} error Thrown value.
 * @returns {boolean} True for the "LinkedIn not connected" 409.
 */
export const isNotConnectedError = (error) => Boolean(error)
    && (error.code === LINKEDIN_NOT_CONNECTED || error.payload?.code === LINKEDIN_NOT_CONNECTED);

/**
 * Tenant scope header, never allowed to fail the request it decorates.
 * @returns {Promise<Record<string, string>>}
 */
const scopeHeaders = async () => {
    try {
        const scope = await getManagementScope();

        return scope?.restaurantId ? {'x-restaurant-id': String(scope.restaurantId)} : {};
    } catch (_e) {
        return {};
    }
};

/**
 * Build a `?a=b` query string from the defined values only.
 * @param {Object<string, *>} [query]
 * @returns {string}
 */
const buildQuery = (query) => {
    const parts = Object.entries(query || {})
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

    return parts.length ? `?${parts.join('&')}` : '';
};

/**
 * @param {string} path Path relative to `/linkedin`.
 * @param {object} [options] `apiFetch` options.
 * @returns {Promise<object>} Parsed body (always an object).
 */
const request = async (path, options = {}) => {
    const body = await apiFetch(`/linkedin${path}`, {
        skipDedup: true,
        skipCache: true,
        ...options,
        headers: {...(await scopeHeaders()), ...(options.headers || {})},
    });

    return body && typeof body === 'object' ? body : {};
};

const post = (path, payload) => request(path, {method: 'POST', body: JSON.stringify(payload || {})});

const linkedinApi = {
    /** @returns {Promise<{connected: boolean, status?: string, needsReconnect?: boolean, account?: object, unreadCount?: number}>} */
    getStatus: () => request('/status'),
    /** @returns {Promise<{unreadCount: number}>} */
    getUnreadCount: () => request('/unread-count'),
    /**
     * @param {{successRedirectUrl?: string, failureRedirectUrl?: string}} [body]
     * @returns {Promise<{url: string}>} Hosted-auth URL.
     */
    connect: (body) => post('/connect', body || {}),
    /** @returns {Promise<object>} */
    disconnect: () => post('/disconnect'),
    /**
     * @param {{cursor?: string, limit?: number, unread?: boolean}} [query]
     * @returns {Promise<{items: object[], nextCursor: ?string}>}
     */
    listConversations: (query) => request(`/conversations${buildQuery(query)}`),
    /**
     * @param {string} id Conversation id.
     * @param {{cursor?: string, limit?: number}} [query]
     * @returns {Promise<{items: object[], nextCursor: ?string}>}
     */
    listMessages: (id, query) => request(`/conversations/${encodeURIComponent(id)}/messages${buildQuery(query)}`),
    /** @param {string} id Conversation id. @returns {Promise<object>} */
    markRead: (id) => post(`/conversations/${encodeURIComponent(id)}/read`),
    /** @param {string} id Conversation id. @param {string} text Reply text. @returns {Promise<object>} */
    reply: (id, text) => post(`/conversations/${encodeURIComponent(id)}/reply`, {text}),
    /**
     * @param {{linkedinUrl?: string, providerId?: string, text: string, entityType?: string, entityId?: string, templateId?: string, variables?: object}} body
     * @returns {Promise<{status: 'sent'|'invite_sent'|'failed', chatId?: string}>}
     */
    send: (body) => post('/send', body),
    /**
     * Render one of the caller's mail templates as LinkedIn plain text (variables filled server-side).
     * @param {{templateId: string, entityType?: string, entityId?: string, variables?: object}} body
     * @returns {Promise<{text: string, templateId: string, name: string}>}
     */
    renderTemplate: (body) => post('/templates/render', body),
    /** @param {{emails?: string[], linkedinUrls?: string[]}} body @returns {Promise<{matches: object[]}>} */
    resolveContacts: (body) => post('/resolve', body),
    /**
     * @param {string} entityType `lead` | `customer` | `contact`.
     * @param {string} entityId Record id.
     * @param {{limit?: number, before?: string}} [query]
     * @returns {Promise<{items: object[], nextBefore: ?string}>}
     */
    listEntityMessages: (entityType, entityId, query) => request(
        `/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/messages${buildQuery(query)}`,
    ),
    /**
     * @param {{kind: 'people'|'companies', query?: string, filters?: object, cursor?: string, limit?: number}} body
     * @returns {Promise<{items: object[], nextCursor: ?string}>}
     */
    search: (body) => post('/search', body),
    /**
     * Save LinkedIn people search hits as CRM leads (source LinkedIn). Unlike the Apollo importer no e-mail or
     * phone is needed; deduped by profile URL, so a person already saved comes back as `duplicate`.
     * @param {object[]} items People hits as returned by {@link search}.
     * @param {{status?: string, ownerId?: string, groupId?: string, tags?: string[]}} [options]
     * @returns {Promise<{created: number, duplicates: number, failed: number, leads: {linkedinUrl: string, leadId: ?string, status: 'created'|'duplicate'|'failed', error?: string}[]}>}
     */
    importProspects: (items, options) => post('/prospects/import', {items, ...(options || {})}),
    /**
     * 1st-degree connections, most recently added first. Only for accounts whose provider supports it
     * (`status.capabilities.connections`), otherwise 422 `LINKEDIN_UNSUPPORTED`.
     * @param {{cursor?: string, limit?: number, search?: string}} [query]
     * @returns {Promise<{items: object[], cursor: ?string}>}
     */
    listConnections: (query) => request(`/connections${buildQuery(query)}`),
    /**
     * Pending connection requests plus the caller's remaining send quota.
     * @param {{direction: 'received'|'sent', cursor?: string, limit?: number}} query
     * @returns {Promise<{items: object[], cursor: ?string, quota: object}>}
     */
    listInvitations: (query) => request(`/invitations${buildQuery(query)}`),
    /**
     * Send one connection request (counts against the daily / weekly cap, 429 when reached).
     * @param {{providerId: string, note?: string}} body
     * @returns {Promise<{status: 'invite_sent', quota: object}>}
     */
    sendInvitation: (body) => post('/invitations', body),
    /**
     * Accept or decline a received connection request.
     * @param {string} id Invitation id.
     * @param {'accept'|'decline'} action
     * @returns {Promise<{ok: boolean, action: string}>}
     */
    respondInvitation: (id, action) => post(`/invitations/${encodeURIComponent(id)}/respond`, {action}),
    /** @param {string} id Sent invitation id. @returns {Promise<{ok: boolean}>} */
    withdrawInvitation: (id) => request(`/invitations/${encodeURIComponent(id)}`, {method: 'DELETE'}),
    /**
     * The caller's LinkedIn posts, newest first.
     * @param {{status?: string, page?: number, limit?: number}} [query]
     * @returns {Promise<{items: object[], total: number}>}
     */
    listPosts: (query) => request(`/posts${buildQuery(query)}`),
    /**
     * Upload one attachment (multipart, field `file`) for a post.
     * @param {{uri: string, name: string, mimeType: string, file?: ?object}} asset Upload descriptor.
     * @returns {Promise<{url: string, type: string, name: string, size: number, mimeType: string, storageKey: string}>}
     */
    uploadPostMedia: async (asset) => {
        const token = await getAccessToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${ApiConfig.apiURL}/api/linkedin/posts/media`, {
            method: 'POST',
            headers: {Authorization: `Bearer ${token}`, Accept: 'application/json', ...(await scopeHeaders())},
            body: await buildAttachmentFormData(asset, asset.name, asset.mimeType),
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok) {
            const error = new Error(payload?.message || payload?.error || response.statusText || 'Upload failed');

            error.status = response.status;
            error.payload = payload;
            error.code = payload?.code || payload?.msg || '';
            throw error;
        }

        return payload || {};
    },
    /** @param {string} id Post id. @returns {Promise<object>} */
    getPost: (id) => request(`/posts/${encodeURIComponent(id)}`),
    /**
     * @param {{text: string, media?: {url: string, type: string}[], visibility?: string, scheduledAt?: string, publishNow?: boolean}} body
     * @returns {Promise<object>} The created post.
     */
    createPost: (body) => post('/posts', body),
    /** Only draft / scheduled / failed posts; `scheduledAt: null` makes a draft. @returns {Promise<object>} */
    updatePost: (id, body) => request(`/posts/${encodeURIComponent(id)}`, {method: 'PUT', body: JSON.stringify(body || {})}),
    /** @param {string} id Post id. @returns {Promise<{deleted: boolean, remoteDeleted: boolean}>} */
    deletePost: (id) => request(`/posts/${encodeURIComponent(id)}`, {method: 'DELETE'}),
    /** @param {string} id Post id. @returns {Promise<object>} */
    publishPost: (id) => post(`/posts/${encodeURIComponent(id)}/publish`),
    /** @param {string} id Post id. @returns {Promise<object>} The post with fresh `stats`. */
    refreshPostStats: (id) => post(`/posts/${encodeURIComponent(id)}/refresh-stats`),
};

/**
 * Start the hosted-auth connect from a user gesture and open the provider page. Opened in the system
 * browser; the screen re-reads {@link linkedinApi.getStatus} when the app returns to the foreground.
 * @returns {Promise<string>} The URL that was opened.
 * @throws {Error} When the server returns no hosted-auth URL.
 */
export const openLinkedinConnect = async () => {
    const res = await linkedinApi.connect({
        successRedirectUrl: LINKEDIN_RETURN_URL,
        failureRedirectUrl: `${LINKEDIN_RETURN_URL}?error=1`,
    });

    if (!res.url) {
        throw new Error('Could not start the LinkedIn connection.');
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.open(res.url, '_blank');
    } else {
        await Linking.openURL(res.url);
    }

    return res.url;
};

export default linkedinApi;
